import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
  Textarea,
  TextareaProps,
  Tooltip,
} from '@chakra-ui/react'
import { InfoIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

interface FormTextareaProps<T extends FieldValues>
  extends Omit<TextareaProps, 'name'> {
  name: Path<T>
  label: string
  control: Control<T, any, any>
  isRequired?: boolean
  tooltip?: string
}

export function FormTextarea<T extends FieldValues>({
  name,
  label,
  control,
  isRequired = false,
  tooltip,
  ...textareaProps
}: FormTextareaProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const charCount = useMemo(() => field.value?.length || 0, [field.value])

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel className="flex gap-2 items-center">
        {label}
        {tooltip && (
          <Tooltip hasArrow label={tooltip}>
            <InfoIcon size={'1rem'} className="text-green-500" />
          </Tooltip>
        )}
      </FormLabel>
      <Textarea {...field} {...textareaProps} />
      <Box
        className={`flex ${
          error?.message ? 'justify-between' : 'justify-end'
        } w-full pr-1 pt-1`}
      >
        <FormErrorMessage>{error?.message}</FormErrorMessage>
        <Text className={`text-xs ${charCount >= 400 ? 'text-green-500' : ''}`}>
          {charCount}/400
        </Text>
      </Box>
    </FormControl>
  )
}
